import { useCallback, useEffect, useState } from "react";
import { Briefcase, Mail, AlertTriangle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function SigningRequestBadges({ entityType, entityId }) {
  const [reqs, setReqs] = useState(null);
  const [resendFor, setResendFor] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/signing-requests/entity/${entityType}/${entityId}`).then((r) => setReqs(r.data)).catch(() => setReqs([]));
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const pending = (reqs || []).filter((r) => r.status === "pending");
  if (pending.length === 0) return null;

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/signing-requests/${resendFor.id}/resend`, { email: email || null });
      toast.success(`Signeringslenke sendt til ${email}`);
      setResendFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 space-y-1">
      {pending.map((r) => (
        <div key={r.id} data-testid={`sreq-${r.id}`} className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {r.mode === "company_task" ? (
            r.target_registered ? (
              <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-primary" /> Oppgave sendt til styreleder i {r.target_company_name}</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> {r.target_company_name} er ikke registrert i Styrerom – de kan ikke få signeringsoppgave</span>
            )
          ) : r.person_email ? (
            <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-primary" /> Signeringslenke {r.notified_at ? "sendt" : "klar"} til {r.person_email}</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> {r.party_name} mangler e-post for signeringslenke</span>
          )}
          {r.mode === "person_link" ? (
            <button className="font-medium text-primary hover:underline" data-testid={`sreq-resend-${r.id}`} onClick={() => { setResendFor(r); setEmail(r.person_email || ""); }}>
              {r.person_email ? "Send på nytt" : "Legg til e-post"}
            </button>
          ) : null}
        </div>
      ))}

      <Dialog open={!!resendFor} onOpenChange={(o) => !o && setResendFor(null)}>
        <DialogContent data-testid="resend-link-dialog">
          <DialogHeader>
            <DialogTitle>Send signeringslenke</DialogTitle>
            <DialogDescription>Send en BankID-signeringslenke på e-post til {resendFor?.party_name}.</DialogDescription>
          </DialogHeader>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="navn@epost.no" data-testid="resend-email-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResendFor(null)}>Avbryt</Button>
            <Button className="gap-2" disabled={busy || !email} onClick={send} data-testid="resend-send-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send lenke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
